// 存档二次校验（EP-002-S3）—— loadSave 的 ok 分支「读到合法对象后」再做一层结构校验。
// 返回 null 表示对象结构非法（corrupt），调用方据此走「备份 + 重置」分支。
// 本函数只做"形状/类型"校验，不负责版本迁移（迁移见 ./migrate）。
import { CURRENT_SCHEMA_VERSION } from './constants';
import type {
  SaveDataV1,
  DogState,
  TripStatus,
  DestinationId,
  DropResultV1,
  TripV1,
  InventoryStackV1,
  PostcardEntry,
} from './types';

const DOG_STATES: DogState[] = ['home', 'away', 'awaitingReveal'];
const TRIP_STATUSES: TripStatus[] = ['inProgress', 'awaitingReveal'];
const DESTINATIONS: DestinationId[] = ['riverside', 'forest', 'hill', 'town'];

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asString(v: unknown): v is string {
  return typeof v === 'string';
}

function asFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function sanitizeDropResult(v: unknown): DropResultV1 | null {
  if (v === null) return null;
  if (!isObject(v)) return null;
  if (!Array.isArray(v.giftIds)) return null;
  if (!v.giftIds.every(asString)) return null;
  if (!asFiniteNumber(v.rolledAt)) return null;
  return { giftIds: v.giftIds, rolledAt: v.rolledAt };
}

function sanitizeTrip(v: unknown): TripV1 | null {
  if (v === null) return null;
  if (!isObject(v)) return null;
  if (!asString(v.id)) return null;
  if (!asFiniteNumber(v.departureTimestamp)) return null;
  if (!asFiniteNumber(v.travelDurationMs)) return null;
  if (!asString(v.destinationId) || !DESTINATIONS.includes(v.destinationId as DestinationId)) return null;
  if (typeof v.isLongTrip !== 'boolean') return null;
  if (!asString(v.status) || !TRIP_STATUSES.includes(v.status as TripStatus)) return null;
  const dropResult = sanitizeDropResult(v.dropResult);
  if (v.dropResult !== null && dropResult === null) return null;
  if (!asString(v.noteId)) return null;
  return {
    id: v.id,
    departureTimestamp: v.departureTimestamp,
    travelDurationMs: v.travelDurationMs,
    destinationId: v.destinationId as DestinationId,
    isLongTrip: v.isLongTrip,
    status: v.status as TripStatus,
    dropResult,
    noteId: v.noteId,
  };
}

function sanitizeInventory(v: unknown): InventoryStackV1[] | null {
  if (!Array.isArray(v)) return null;
  const out: InventoryStackV1[] = [];
  for (const item of v) {
    if (!isObject(item)) return null;
    if (!asString(item.giftId)) return null;
    if (typeof item.count !== 'number' || !Number.isInteger(item.count) || item.count < 1) return null;
    if (!asFiniteNumber(item.firstObtainedAt)) return null;
    out.push({ giftId: item.giftId, count: item.count, firstObtainedAt: item.firstObtainedAt, isRead: true });
    // 🆕 v2.1：存量 inventory 无 isRead 字段 → 视为已读（不显示未读角标）。
    // 新入库礼物由 upsertStacks 注入 isRead: false（驱动角标）。
    // 新增字段不进 schemaVersion 升（加在 additive 路径，与 isRead on PostcardEntry 同策略）。
  }
  return out;
}

function sanitizeLastNoteMap(v: unknown): Record<string, string> {
  if (!isObject(v)) return {};
  const out: Record<string, string> = {};
  for (const key of Object.keys(v)) {
    if (asString(v[key])) out[key] = v[key] as string;
  }
  return out;
}

// 🆕 v2：明信片条目逐条校验（与 InventoryStackV1 同等待遇）。
// 🆕 STEP 5-B：isRead 是 v2.1 起的"additive"字段——旧 v2.0 存档无此字段 → 默认 false（视为未读），
//   不破坏向后兼容，schemaVersion 仍 = 2。
function sanitizePostcard(v: unknown): PostcardEntry | null {
  if (!isObject(v)) return null;
  if (!asString(v.id)) return null;
  if (!asString(v.tripId)) return null;
  if (!asString(v.destinationId) || !DESTINATIONS.includes(v.destinationId as DestinationId)) return null;
  if (!asString(v.noteId)) return null;
  if (!asFiniteNumber(v.stampedAt)) return null;
  if (!asFiniteNumber(v.receivedAt)) return null;
  // imageAsset 允许 null（图暂未到，UI 降级到占位）
  if (v.imageAsset !== null && !asString(v.imageAsset)) return null;
  // 🆕 isRead 字段：v2.1 起为必填；旧 v2.0 存档无此字段 → 默认 false（additive 路径）
  const isRead = typeof v.isRead === 'boolean' ? v.isRead : false;
  return {
    id: v.id,
    tripId: v.tripId,
    destinationId: v.destinationId as DestinationId,
    noteId: v.noteId,
    stampedAt: v.stampedAt,
    receivedAt: v.receivedAt,
    imageAsset: v.imageAsset as string | null,
    isRead,
  };
}

function sanitizeMailbox(v: unknown): PostcardEntry[] | null {
  if (!Array.isArray(v)) return null;
  const out: PostcardEntry[] = [];
  for (const item of v) {
    const sanitized = sanitizePostcard(item);
    if (sanitized === null) return null;
    out.push(sanitized);
  }
  return out;
}

// 🆕 v2：明信片按目的地去重键。值允许为 null（首寄或显式清空）；键仅限已知目的地。
function sanitizeLastPostcardMap(v: unknown): Partial<Record<DestinationId, string | null>> | null {
  if (!isObject(v)) return null;
  const out: Partial<Record<DestinationId, string | null>> = {};
  for (const key of Object.keys(v)) {
    if (!DESTINATIONS.includes(key as DestinationId)) return null;
    const val = v[key];
    if (val === null) {
      out[key as DestinationId] = null;
    } else if (asString(val)) {
      out[key as DestinationId] = val;
    } else {
      return null;
    }
  }
  return out;
}

// 二次校验入口：读到合法 JSON 对象后调用。结构非法返回 null（→ corrupt）。
export function sanitizeV1(save: unknown): SaveDataV1 | null {
  if (!isObject(save)) return null;
  if (save.schemaVersion !== CURRENT_SCHEMA_VERSION) return null;
  if (!asString(save.dogState) || !DOG_STATES.includes(save.dogState as DogState)) return null;

  const trip = sanitizeTrip(save.trip);
  if (save.trip !== null && trip === null) return null;

  if (save.lastSettledTripId !== null && !asString(save.lastSettledTripId)) return null;

  if (!isObject(save.stats)) return null;
  if (!asFiniteNumber(save.stats.tripCount) || !Number.isInteger(save.stats.tripCount) || save.stats.tripCount < 0) return null;
  if (typeof save.stats.isFirstTripDone !== 'boolean') return null;

  if (!isObject(save.settings)) return null;
  if (typeof save.settings.timeScale !== 'number' || !(save.settings.timeScale > 0)) return null;
  if (typeof save.settings.duskTintEnabled !== 'boolean') return null;

  const inventory = sanitizeInventory(save.inventory);
  if (inventory === null) return null;

  if (!asFiniteNumber(save.savedAt)) return null;

  // 🆕 v2：mailbox + lastPostcardNoteIdByDestination 必填校验。
  const mailbox = sanitizeMailbox(save.mailbox);
  if (mailbox === null) return null;
  const lastPostcardNoteIdByDestination = sanitizeLastPostcardMap(save.lastPostcardNoteIdByDestination);
  if (lastPostcardNoteIdByDestination === null) return null;

  // 该字段读取后透传到返回对象；写入由 src/domain/settleTrip.ts 独占（CI 守门 check-settle-ownership）。
  // 此处 const 变量名与字段同名是为了下方对象字面量 shorthand 写法。
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const lastSettledTripId = (save.lastSettledTripId as string | null) ?? null;

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    dogState: save.dogState as DogState,
    trip,
    lastSettledTripId,
    stats: { tripCount: save.stats.tripCount, isFirstTripDone: save.stats.isFirstTripDone },
    lastNoteIdByDestination: sanitizeLastNoteMap(save.lastNoteIdByDestination),
    settings: { timeScale: save.settings.timeScale, duskTintEnabled: save.settings.duskTintEnabled },
    inventory,
    savedAt: save.savedAt,
    mailbox,
    lastPostcardNoteIdByDestination,
    // 🆕 P6-POLISH-ENG-002 §B.4（additive 可选字段，不升 schemaVersion）：
    //   **只在旧档真的写了这个 key 时才透传**；没写就保持"键不存在"。
    //   两个理由：
    //   ① 读取侧统一用 `lastReturnWasEmpty === true` 判定，undefined 与 false 语义完全等价，
    //     没必要强行补写；
    //   ② 补写会让「存→读往返」平白多出一个 key（loadSave 往返测试是深比较）。
    //      不为兼容而制造 key-count 差异 —— 这正是选 optional 而非 required 类型的原因。
    //   脏值（非 boolean）同样按"不存在"处理 → 读取侧落到 false，不因它判 corrupt。
    ...(typeof save.lastReturnWasEmpty === 'boolean'
      ? { lastReturnWasEmpty: save.lastReturnWasEmpty }
      : {}),
  };
}
